import type { Icon } from "@phosphor-icons/react";
import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { ChatIcon } from "@phosphor-icons/react/dist/csr/Chat";
import { ChatSlashIcon } from "@phosphor-icons/react/dist/csr/ChatSlash";
import { EnvelopeIcon } from "@phosphor-icons/react/dist/csr/Envelope";
import { EnvelopeSimpleIcon } from "@phosphor-icons/react/dist/csr/EnvelopeSimple";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { DELIVERY_PALETTE } from "@/core/colors";
import {
  isPayrollStatementDeliveryFailed,
  isPayrollStatementDeliverySuccessful,
  isTextOnlyDelivery,
} from "./delivery-status-rules";
import type {
  PayrollStatementNotificationDelivery,
  PayrollStatementSmsDelivery,
} from "./types";
import {
  formatTextNumber,
  getDeliveryStatusLabel,
  getNotificationReasonLabel,
  getSmsDeliveryReason,
  getSmsDeliveryStatusLabel,
} from "./utils";

type ChannelTone = "success" | "failure" | "neutral";

interface Channel {
  key: "email" | "text";
  name: string;
  Icon: Icon;
  tone: ChannelTone;
  status: string;
  destination: string | null;
  detail: string | null;
}

const TONE_CLASS: Record<ChannelTone, string> = {
  success: DELIVERY_PALETTE.sent.foreground,
  failure: DELIVERY_PALETTE.failed.foreground,
  neutral: "text-subtle",
};

const emailChannel = (
  delivery: PayrollStatementNotificationDelivery,
): Channel => {
  const failed = isPayrollStatementDeliveryFailed(delivery.status);
  return {
    key: "email",
    name: "Email",
    Icon: failed ? EnvelopeSimpleIcon : EnvelopeIcon,
    tone: failed
      ? "failure"
      : isPayrollStatementDeliverySuccessful(delivery.status)
        ? "success"
        : "neutral",
    status: getDeliveryStatusLabel(delivery.status),
    destination: delivery.recipient_email ?? null,
    detail: delivery.error
      ? (getNotificationReasonLabel(delivery.error) ?? delivery.error)
      : null,
  };
};

const textChannel = (sms: PayrollStatementSmsDelivery): Channel => ({
  key: "text",
  name: "Text",
  Icon:
    sms.status === "failed"
      ? ChatSlashIcon
      : sms.status === "skipped"
        ? ChatSlashIcon
        : ChatIcon,
  tone:
    sms.status === "failed"
      ? "failure"
      : sms.status === "succeeded"
        ? "success"
        : "neutral",
  status: getSmsDeliveryStatusLabel(sms),
  destination: sms.to ? formatTextNumber(sms.to) : null,
  detail: getSmsDeliveryReason(sms),
});

/**
 * Every way this PayPack went out, email first. A text-only send has no email to
 * show, so it lists the text alone.
 */
export const getDeliveryChannels = (
  delivery: PayrollStatementNotificationDelivery,
): Channel[] => {
  const email = isTextOnlyDelivery(delivery) ? [] : [emailChannel(delivery)];
  return delivery.sms ? [...email, textChannel(delivery.sms)] : email;
};

/**
 * How a PayPack went out, at a glance: one icon per channel, coloured by how that
 * channel did, so a failed text stands out beside a sent email without a line of
 * its own. Opens to the per-channel detail.
 *
 * Stops click propagation: the row opens the technician's sidebar, and opening
 * this should not.
 */
export const DeliveryChannels = ({
  delivery,
}: {
  delivery: PayrollStatementNotificationDelivery;
}) => {
  const channels = getDeliveryChannels(delivery);
  const summary = channels
    .map((channel) => `${channel.name}: ${channel.status}`)
    .join(", ");
  const failed = channels.filter((channel) => channel.tone === "failure");
  const partlyFailed = failed.length > 0 && failed.length < channels.length;

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              aria-label={`How it was sent. ${summary}`}
              onClick={(event) => event.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-neutral-hovered focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus focus-visible:outline-solid"
            />
          }
        >
          {channels.map(({ key, Icon, tone }) => (
            <Icon
              key={key}
              aria-hidden
              className={cn("h-3.5 w-3.5", TONE_CLASS[tone])}
            />
          ))}
          {partlyFailed && (
            <span
              aria-hidden
              className={cn("text-xs", DELIVERY_PALETTE.failed.foreground)}
            >
              {failed.map((channel) => channel.name).join(" and ")} failed
            </span>
          )}
          <CaretDownIcon aria-hidden className="h-3 w-3 text-subtle" />
        </TooltipTrigger>
        <TooltipContent>{summary}</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-72 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <Stack align="start" gap={4}>
          {channels.map(
            ({ key, name, Icon, tone, status, destination, detail }) => (
              <Stack
                className="w-full"
                key={key}
                direction="row"
                gap={2}
                align="start"
              >
                <Icon
                  aria-hidden
                  className={cn("mt-0.5 h-4 w-4 shrink-0", TONE_CLASS[tone])}
                />
                <Stack align="start" gap={1} className="min-w-0 flex-1">
                  <Stack
                    className="w-full"
                    align="start"
                    direction="row"
                    justify="between"
                    gap={2}
                  >
                    <Text variant="label">{name}</Text>
                    <Text
                      variant="caption"
                      tone={tone === "failure" ? "destructive" : "muted"}
                    >
                      {status}
                    </Text>
                  </Stack>
                  {destination && (
                    <Text
                      variant="caption"
                      tone="muted"
                      className="truncate"
                      title={destination}
                    >
                      {destination}
                    </Text>
                  )}
                  {detail && (
                    <Text
                      variant="caption"
                      tone={tone === "failure" ? "destructive" : "muted"}
                      className="wrap-break-word"
                    >
                      {detail}
                    </Text>
                  )}
                </Stack>
              </Stack>
            ),
          )}
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
